import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Phone } from 'lucide-react';
import type { FollowUpQueueItem } from '@tailoring-crm/shared';
import { Badge, Button, cn } from '@tailoring-crm/ui';
import { followUpState } from '../../lib/follow-ups/follow-up-state.js';
import { useSetCustomerTaskStatus } from '../../lib/follow-ups/use-follow-ups.js';
import { formatDate, formatMobile } from '../../lib/format.js';

/**
 * One owed follow-up: a customer's open task, with the customer's name and number.
 *
 * Shared by the full page and the header panel ON PURPOSE. Both are read in order to make a
 * phone call and tick the job off, and two copies of that row is how the tick ends up behaving
 * differently in the two places it is offered.
 *
 * `compact` is a layout switch, not a feature switch: the panel is 384px wide, so the date and
 * the number drop to their own line rather than being squeezed onto the title's. Nothing is
 * taken away — a follow-up you cannot complete from the panel is a panel you stop opening.
 */
export function FollowUpRow({
  item,
  onDone,
  compact = false,
}: {
  item: FollowUpQueueItem;
  onDone: () => void;
  compact?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  // Bound to the row's own customer, because a queue spans many and each completion has to go
  // to — and invalidate — the right one.
  const customerId = item.customerId ?? '';
  const setStatus = useSetCustomerTaskStatus(customerId);
  const due = followUpState(item.dueAt);

  const dueClass = cn(
    'whitespace-nowrap tabular-nums',
    compact ? 'text-xs' : 'text-sm',
    due === 'overdue' && 'font-medium text-destructive',
    due === 'today' && 'font-medium text-amber-700 dark:text-amber-400',
  );

  return (
    <li
      className={cn(
        'activity-task flex gap-2 rounded-lg border border-border',
        compact ? 'p-2' : 'flex-col p-3 sm:flex-row sm:items-center sm:gap-4',
      )}
    >
      <button
        type="button"
        aria-label={t('followUps.completeAction')}
        disabled={setStatus.isPending}
        onClick={() => setStatus.mutate({ taskId: item.id, status: 'done' }, { onSuccess: onDone })}
        className={cn(
          'inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-input',
          compact && 'mt-0.5',
        )}
      >
        <Check aria-hidden="true" className="size-3.5 opacity-0" />
      </button>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{item.name}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <Link to={`/customers/${customerId}`} className="font-medium hover:underline">
            {item.subjectName}
          </Link>
          {item.categoryLabel ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{item.categoryLabel}</span>
            </>
          ) : null}
        </div>

        {/* In the panel the date and the number sit under the name; there is no room beside it. */}
        {compact ? (
          <div className="mt-1.5 flex items-center gap-2">
            {item.priority === 'high' ? (
              <Badge variant="outline">{t('followUps.highPriority')}</Badge>
            ) : null}
            <span className={dueClass}>{formatDate(item.dueAt)}</span>
            {/* The point of the row: this panel is opened in order to make a phone call. */}
            <Button asChild variant="outline" size="sm" className="ml-auto h-7 px-2">
              <a href={`tel:${item.subjectPhone}`}>
                <Phone aria-hidden="true" className="size-3.5" />
                {formatMobile(item.subjectPhone)}
              </a>
            </Button>
          </div>
        ) : null}
      </div>

      {compact ? null : (
        <div className="flex items-center gap-3">
          {item.priority === 'high' ? (
            <Badge variant="outline">{t('followUps.highPriority')}</Badge>
          ) : null}
          <span className={dueClass}>{formatDate(item.dueAt)}</span>
          {/* The point of the row: this screen is opened in order to make a phone call. */}
          <Button asChild variant="outline" size="sm">
            <a href={`tel:${item.subjectPhone}`}>
              <Phone aria-hidden="true" className="size-4" />
              {formatMobile(item.subjectPhone)}
            </a>
          </Button>
        </div>
      )}
    </li>
  );
}
